import { RotateCcw } from 'lucide-react';
import { useMemo, useState } from 'react';
import type { Permission, PermissionEffect, UserPermissions } from '#shared/types';
import { Banner } from '#client/components/ui/Banner';
import { Button } from '#client/components/ui/Button';
import { Card } from '#client/components/ui/Card';
import { RoleBadge } from '#client/components/ui/StatusBadge';
import { useCan } from '#client/hooks/useCan';
import { useMutation } from '#client/hooks/useMutation';
import { useApiResource } from '#client/hooks/useResourceItem';
import { ENDPOINT } from '#client/lib/endpoints';
import { useAuth } from '#client/providers/AuthProvider';
import { useToast } from '#client/providers/ToastProvider';
import {
  PermissionHistory,
  PermissionMatrix,
  effectiveWith,
  overridesPayload,
} from '#client/features/roles';
import { saveUserOverrides } from '#client/features/roles';

/**
 * What one staff member may do: their role's permissions, plus exceptions.
 *
 * Read-only for `staff.view`; editable with `staff.permissions`. An exception
 * is for the one person who needs something the rest of their role does not —
 * if several people need it, the answer is a new role.
 */
export function UserPermissionsCard({ userId }: { userId: number }) {
  const request = useApiResource<UserPermissions>(ENDPOINT.userPermissions(userId));

  if (request.error) {
    return (
      <Card title="Permissions">
        <Banner tone="critical">{request.error}</Banner>
      </Card>
    );
  }
  if (!request.data) return <Card title="Permissions">Loading…</Card>;

  // Keyed by the loaded data so a save remounts from what the server stored.
  return (
    <UserPermissionsEditor
      key={JSON.stringify(request.data.overrides)}
      userId={userId}
      data={request.data}
      onSaved={request.refresh}
    />
  );
}

function UserPermissionsEditor({
  userId,
  data,
  onSaved,
}: {
  userId: number;
  data: UserPermissions;
  onSaved: () => void;
}) {
  const can = useCan();
  const { user } = useAuth();
  const toast = useToast();
  const mutation = useMutation('Could not save the permissions.');
  const initial = useMemo(
    () =>
      new Map<Permission, PermissionEffect>(
        data.overrides.map((override) => [override.permission, override.effect]),
      ),
    [data.overrides],
  );
  const [overrides, setOverrides] = useState<Map<Permission, PermissionEffect>>(initial);

  const rolePermissions = useMemo(() => new Set(data.role_permissions), [data.role_permissions]);
  const effective = useMemo(
    () =>
      data.role.is_super
        ? new Set(data.effective)
        : effectiveWith(data.role_permissions, overrides),
    [data, overrides],
  );
  const isSelf = user?.id === userId;
  const editable = can('staff.permissions') && !data.role.is_super && !isSelf;
  const dirty = changed(initial, overrides);

  const handleSave = async () => {
    const outcome = await mutation.run(() =>
      saveUserOverrides(userId, { overrides: overridesPayload(overrides) }),
    );
    if (!outcome.ok) return;
    toast.success('Permissions saved');
    onSaved();
  };

  return (
    <>
      <Card
        title="Permissions"
        subtitle={
          <>
            Role <RoleBadge role={data.role} /> · {overrides.size} exception
            {overrides.size === 1 ? '' : 's'} · {effective.size} permissions in total
          </>
        }
        actions={
          editable ? (
            <ExceptionActions
              canReset={overrides.size > 0}
              canSave={dirty}
              saving={mutation.isPending}
              onReset={() => setOverrides(new Map())}
              onSave={() => void handleSave()}
            />
          ) : undefined
        }
      >
        {mutation.errorMessage && <Banner tone="critical">{mutation.errorMessage}</Banner>}
        <ExceptionNotice isSuper={data.role.is_super} isSelf={isSelf} />
        <PermissionMatrix
          mode="override"
          rolePermissions={rolePermissions}
          overrides={overrides}
          effective={effective}
          onChange={editable ? setOverrides : undefined}
        />
      </Card>
      {can('staff.permissions') && (
        <PermissionHistory path={ENDPOINT.userPermissionsHistory(userId)} />
      )}
    </>
  );
}

/** Why the matrix cannot be changed, when it cannot. */
function ExceptionNotice({ isSuper, isSelf }: { isSuper: boolean; isSelf: boolean }) {
  if (isSuper) {
    return (
      <Banner tone="info">
        This person holds the admin role, which has every permission. Exceptions do not apply.
      </Banner>
    );
  }
  if (isSelf) {
    return <Banner tone="info">You cannot change your own permissions. Ask another admin.</Banner>;
  }
  return null;
}

function changed(
  before: ReadonlyMap<Permission, PermissionEffect>,
  after: ReadonlyMap<Permission, PermissionEffect>,
): boolean {
  return (
    before.size !== after.size || [...after].some(([key, effect]) => before.get(key) !== effect)
  );
}

function ExceptionActions({
  canReset,
  canSave,
  saving,
  onReset,
  onSave,
}: {
  canReset: boolean;
  canSave: boolean;
  saving: boolean;
  onReset: () => void;
  onSave: () => void;
}) {
  return (
    <div className="c-perm-actions">
      <Button icon={<RotateCcw />} disabled={!canReset} onClick={onReset}>
        Reset to role
      </Button>
      <Button variant="primary" disabled={!canSave} loading={saving} onClick={onSave}>
        Save permissions
      </Button>
    </div>
  );
}
